import { Button } from '@/components/atoms/NewPcButton';
import {
  LayoutTemplateContainer,
  LayoutTemplateController,
  LayoutTemplateTitle,
} from '../../_layouts/LayoutsEditTemplate';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/utils/tailwind';
import { useReviews } from './ReviewsProvider';
import { useToast } from '@/hooks/V3/useToast';

interface TemplateProps {
  title: string;
  setTitle: (v: string) => void;
  content: string;
  setContent: (v: string) => void;
  onBack: () => void;
  mode: 'add' | 'edit';
  editTemplateNo?: number | null;
}

export const AddOrEditTemplate = ({
  title,
  setTitle,
  content,
  setContent,
  onBack,
  mode,
  editTemplateNo,
}: TemplateProps) => {
  const { handleAddOrEditTemplate, handleDeleteTemplate, isTemplateSaving, isTemplateDeleting } =
    useReviews();
  const { showToast } = useToast();

  // 입력값이 모두 채워져야만 등록/수정 가능
  const isSubmitDisabled =
    !title.trim() || !content.trim() || isTemplateSaving || isTemplateDeleting;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    handleAddOrEditTemplate(
      {
        Title: title,
        Content: content,
        ...(mode === 'edit' && editTemplateNo ? { TemplateNo: editTemplateNo } : {}),
      },
      () => {
        showToast('success', mode === 'add' ? '템플릿이 추가되었어요' : '템플릿이 수정되었어요.');
        onBack();
      },
    );
  };

  const handleDelete = () => {
    if (!editTemplateNo) return;
    handleDeleteTemplate({ TemplateNo: editTemplateNo }, () => {
      showToast('success', '템플릿이 삭제되었어요.');
      onBack();
    });
  };

  return (
    <LayoutTemplateContainer>
      <div className="flex flex-1 flex-col gap-[12px]">
        <Header mode={mode} onBack={onBack} />
        <Body title={title} setTitle={setTitle} content={content} setContent={setContent} />
      </div>
      <Controller
        mode={mode}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onCancel={onBack}
        isLoading={isTemplateSaving || isTemplateDeleting}
        isSubmitDisabled={isSubmitDisabled}
      />
    </LayoutTemplateContainer>
  );
};

export const Header = ({ mode, onBack }: { mode: 'add' | 'edit'; onBack: () => void }) => (
  <LayoutTemplateTitle>
    <button className="flex items-center" onClick={onBack} aria-label="뒤로가기">
      <ChevronLeft className="h-6 w-6" />
      <div className="">{mode === 'add' ? '템플릿 추가' : '템플릿 목록'}</div>
    </button>
  </LayoutTemplateTitle>
);

export const Body = ({
  title,
  setTitle,
  content,
  setContent,
}: Pick<TemplateProps, 'title' | 'setTitle' | 'content' | 'setContent'>) => {
  return (
    <div className={cn('flex h-full flex-col gap-2 pb-[20px] pt-[10px]')}>
      <input
        className={cn(
          'rounded-[8px]  border border-gr-300 bg-transparent px-[10px] py-[9px] disabled:opacity-50',
          'texty-gr-800 Body5M14 placeholder:text-gr-400',
          'outline-none focus:border focus:border-point-400',
        )}
        placeholder="템플릿 제목을 입력해 주세요"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onCompositionEnd={(e) => {
          setTitle(e.currentTarget.value);
        }}
      />
      <textarea
        className={cn(
          'h-[222px] flex-1 resize-none rounded-[8px] border border-[#ececf2] bg-white px-4 py-3 text-[15px] outline-none placeholder:text-[#b5b5be]',
          'outline-none focus:border-point-400',
        )}
        placeholder="내용을 입력해 주세요"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onCompositionEnd={(e) => {
          setContent(e.currentTarget.value);
        }}
      />
    </div>
  );
};
// Controller 컴포넌트에 isSubmitDisabled prop 추가
export const Controller = ({
  mode,
  onSubmit,
  onDelete,
  onCancel,
  isLoading,
  isSubmitDisabled,
}: {
  mode: 'add' | 'edit';
  onSubmit: () => void;
  onDelete?: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  isSubmitDisabled?: boolean;
}) => (
  <LayoutTemplateController>
    {mode === 'add' ? (
      <Button variant="secondary" className="h-[44px] flex-1 Body5M14" onClick={onCancel}>
        취소
      </Button>
    ) : (
      <Button
        variant="secondary"
        className="h-[44px] flex-1 Body5M14"
        onClick={onDelete}
        disabled={isLoading}
      >
        삭제
      </Button>
    )}
    <Button
      variant="solid"
      className="h-[44px] flex-1 Body5M14"
      onClick={onSubmit}
      disabled={isSubmitDisabled}
      isLoading={isLoading}
    >
      {mode === 'add' ? '등록' : '수정'}
    </Button>
  </LayoutTemplateController>
);
